import { useEffect, useState } from "react";
import { useLocation } from "react-router-dom";

import { listTasks } from "../api/tasks";
import { useAuth } from "../auth/auth-context";
import { across, useShown } from "../shell/locations";
import { useModules } from "../shell/modules-context";

/**
 * **The sidebar's count on Tasks** (TK-D38): the open, awake tasks the reader
 * can act on — theirs, and their roles' unclaimed ones — at the locations shown
 * in the top bar's picker, one request per location (UR-D11). A task they made
 * and gave to someone else is followed, not acted on, so it is not counted.
 *
 * Null while the clinic has no Tasks preview, or the count cannot be read.
 * Re-read on every navigation, as the Requests count is.
 */
export function useOpenTasks(): number | null {
  const on = useModules().hasPreview("TASKS");
  const me = useAuth().user?.id ?? null;
  const shown = useShown();
  const { pathname } = useLocation();
  const [count, setCount] = useState<number | null>(null);

  useEffect(() => {
    if (!on || me === null || shown.ids.length === 0) {
      setCount(null);
      return;
    }
    const controller = new AbortController();
    across(shown.ids, () => listTasks("open", "mine", controller.signal))
      .then((rows) =>
        setCount(
          rows.filter((t) => t.assignee?.id === me || (t.assignee === null && t.canTake)).length,
        ),
      )
      .catch(() => {
        if (!controller.signal.aborted) setCount(null);
      });
    return () => controller.abort();
  }, [on, me, shown.key, pathname]);

  return count;
}
